import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useT } from "@/i18n/LanguageContext";
import { findColor } from "@/constants/colors";
import { printStopLabel } from "@/lib/printStopLabel";

export const CANCEL_STOP_ROLES = ["packing_orders", "inventory_packing", "back_office", "super_admin"];
// Who confirms the STOP = who physically holds the box: CHINA → Packing Orders; CANADA / USA → Inventory Packing.
export const stopStationForWarehouse = (wh) => (String(wh || "").toLowerCase() === "china" ? "packing_orders" : "inventory_packing");
export const STOP_STATION_LABEL = { packing_orders: "Packing Orders (China)", inventory_packing: "Inventory Packing (Canada)" };
export const canConfirmCancelStop = (user, wh) =>
  user?.role === "super_admin" || user?.role === "back_office" || user?.role === stopStationForWarehouse(wh);

const FLAG = { china: "🇨🇳", usa: "🇺🇸", canada: "🇨🇦" };
const WAREHOUSE_LABEL = { china: "CHINA WAREHOUSE", usa: "AMERICA WAREHOUSE", canada: "CANADIAN WAREHOUSE" };

/** Full canonical colour name; in Chinese mode both languages ("Red · 红色"). Custom colours print as typed. */
export function stopColorLabel(color, lang) {
  const c = findColor(color);
  if (!c) return color || "—";
  return lang === "zh" && c.zh ? `${c.name} · ${c.zh}` : c.name;
}

/** Full-screen red STOP for a cancelled order whose set stays in a warehouse.
 *  The station ticks "Catalog # written" + "colour written" and confirms → +1 stock, order → Cancelled Orders. */
export function CancelStockStopDialog({ orderId, open, onOpenChange, onDone }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { t, lang } = useT();
  const [cat, setCat] = useState(false);
  const [col, setCol] = useState(false);
  useEffect(() => { if (open) { setCat(false); setCol(false); } }, [open, orderId]);
  const { data: o, isLoading } = useQuery({
    queryKey: ["order", orderId],
    queryFn: async () => (await api.get(`/orders/${orderId}`)).data,
    enabled: !!orderId && !!open,
  });
  const confirm = useMutation({
    mutationFn: () => api.post(`/orders/${orderId}/cancel-stock-confirm`, { cat_written: cat, color_written: col }),
    onSuccess: (r) => {
      qc.invalidateQueries();
      toast.success(`${t("Stored in")} ${t(WAREHOUSE_LABEL[r?.data?.warehouse] || "")} · ${t("Catalog #")} ${r?.data?.cat_number} · ${stopColorLabel(r?.data?.color, lang)} — ${t("order moved to Cancelled Orders")}`);
      onOpenChange?.(false);
      onDone?.(r?.data);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not confirm"),
  });
  if (!open) return null;
  const stop = o?.cancel_stop;
  const sticker = stop?.sticker;
  const wrongStation = !!stop && !canConfirmCancelStop(user, stop.warehouse);
  const whLabel = stop ? t(stop.warehouse_label) : "";
  const colorLabel = stop ? stopColorLabel(stop.color, lang) : "—";
  const onBox = sticker ? t("on the printed label") : t("on the box");
  const printLabel = () => {
    if (printStopLabel({ po: o.po_number, cat: stop.cat_number, color: stop.color, warehouse: stop.warehouse, lang })) toast.success(t("Label sent to the printer window"));
    else toast.error(t("Popup blocked — allow popups for this site, then print again"));
  };
  return createPortal(
    <div className="fixed inset-0 z-[200] bg-red-700 text-white overflow-y-auto" data-testid="cancel-stock-stop" role="alertdialog" aria-modal="true">
      <div className="min-h-full flex flex-col items-center justify-center px-6 py-10 text-center">
        {isLoading || !o ? (
          <div className="text-2xl font-black">{t("Loading…")}</div>
        ) : !stop ? (
          <>
            <div className="text-3xl font-black">{t("Nothing to store on")} {o.po_number}</div>
            <Button className="mt-6 bg-white text-red-700 hover:bg-red-100" onClick={() => onOpenChange?.(false)} data-testid="cancel-stock-stop-close">{t("Close")}</Button>
          </>
        ) : (
          <>
            <div className="text-[96px] leading-none" aria-hidden>🛑</div>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase" data-testid="cancel-stock-stop-title">{t("STOP")}</h1>
            <p className="mt-3 text-xl sm:text-2xl font-bold uppercase tracking-wide max-w-3xl" data-testid="cancel-stock-stop-headline">
              {o.po_number} {t("is cancelled — this order will stay in the")} {FLAG[stop.warehouse] || ""} {whLabel}
            </p>
            <p className="mt-2 text-base sm:text-lg text-red-100 max-w-2xl">
              {sticker
                ? <>{t("Do NOT ship it. Print the label (Catalog # + colour), stick it on the box")}, {t("put the box on the")} {whLabel} {t("shelf, then confirm below.")}</>
                : <>{t("Do NOT ship it. Write the Catalog number and the full colour name")} {onBox}, {t("put the box on the")} {whLabel} {t("shelf, then confirm below.")}</>}
            </p>

            {sticker && !wrongStation && (
              <Button className="mt-5 h-12 px-6 text-base font-black bg-white text-slate-900 hover:bg-slate-100" onClick={printLabel} data-testid="cancel-stock-stop-print-label">
                🖨 {t("Print label")} · {stop.cat_number || "—"} · {colorLabel}
              </Button>
            )}

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
              <div className="rounded-2xl bg-white text-slate-900 p-6">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">{t("Catalog number")}</div>
                <div className="mt-1 text-3xl sm:text-4xl font-black font-mono break-words" data-testid="cancel-stock-stop-cat">{stop.cat_number || "— MISSING —"}</div>
              </div>
              <div className="rounded-2xl bg-white text-slate-900 p-6">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">{t("Colour")}</div>
                <div className="mt-1 text-3xl sm:text-4xl font-black" data-testid="cancel-stock-stop-color">{colorLabel}</div>
              </div>
            </div>

            {wrongStation ? (
              <div className="mt-8 rounded-xl bg-yellow-300 text-yellow-950 px-5 py-4 max-w-2xl font-semibold" data-testid="cancel-stock-stop-wrong-station">
                {t("This set is in the")} {FLAG[stop.warehouse] || ""} {whLabel} — <b>{t(STOP_STATION_LABEL[stopStationForWarehouse(stop.warehouse)])}</b> {t("confirms it, not your station.")}
              </div>
            ) : stop.missing_catalog ? (
              <div className="mt-8 rounded-xl bg-yellow-300 text-yellow-950 px-5 py-4 max-w-2xl font-semibold" data-testid="cancel-stock-stop-missing-cat">
                ⚠ {t("This order has no Catalog # — Back Office must add it before the set can be stored. Keep the box aside; the confirm is locked.")}
              </div>
            ) : !stop.due ? (
              <div className="mt-8 rounded-xl bg-yellow-300 text-yellow-950 px-5 py-4 max-w-2xl font-semibold" data-testid="cancel-stock-stop-not-due">
                {t("Finish packing / mark the order received first — the confirm unlocks then.")}
              </div>
            ) : (
              <div className="mt-8 w-full max-w-2xl text-left space-y-3">
                <label className={`flex items-center gap-3 rounded-xl px-5 py-4 cursor-pointer text-lg font-semibold ${cat ? "bg-emerald-500 text-white" : "bg-red-800/60 text-white"}`} data-testid="cancel-stock-stop-check-cat">
                  <input type="checkbox" className="h-6 w-6 accent-emerald-600" checked={cat} onChange={(e) => setCat(e.target.checked)} />
                  {sticker ? <>{t("Label with")} {t("Catalog #")} <span className="font-mono">{stop.cat_number}</span> {t("printed and stuck on the box")}</> : <>{t("Catalog #")} <span className="font-mono">{stop.cat_number}</span> {t("written")} {onBox}</>}
                </label>
                <label className={`flex items-center gap-3 rounded-xl px-5 py-4 cursor-pointer text-lg font-semibold ${col ? "bg-emerald-500 text-white" : "bg-red-800/60 text-white"}`} data-testid="cancel-stock-stop-check-color">
                  <input type="checkbox" className="h-6 w-6 accent-emerald-600" checked={col} onChange={(e) => setCol(e.target.checked)} />
                  {sticker ? <>{t("Colour")} <span>{colorLabel}</span> {t("is on the label")}</> : <>{t("Colour")} <span>{colorLabel}</span> {t("written")} {onBox}</>}
                </label>
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Button
                className="h-14 px-8 text-lg font-black bg-white text-red-700 hover:bg-red-100 disabled:opacity-40"
                disabled={wrongStation || !stop.due || stop.missing_catalog || !cat || !col || confirm.isPending}
                onClick={() => confirm.mutate()}
                data-testid="cancel-stock-stop-confirm"
              >
                {confirm.isPending ? t("Saving…") : `✓ ${t("Done — stored in")} ${whLabel}`}
              </Button>
              <Button variant="ghost" className="h-14 px-6 text-white hover:bg-red-800" onClick={() => onOpenChange?.(false)} data-testid="cancel-stock-stop-later">
                {t("Later")}
              </Button>
            </div>
            <div className="mt-6 text-xs text-red-200">
              {t("Cancelled by")} {stop.requested_by || "—"}{stop.requested_at ? ` · ${new Date(stop.requested_at).toLocaleString()}` : ""}{stop.reason ? ` · ${stop.reason}` : ""}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

/** Small red button that opens the STOP for a cancel-pending order (lists, dashboard, order page). */
export function CancelStopButton({ orderId, due = true, size = "sm", className = "", onDone }) {
  const [open, setOpen] = useState(false);
  const { t } = useT();
  return (
    <>
      <Button
        size={size}
        className={`bg-red-600 hover:bg-red-700 text-white font-black ${className}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        data-testid={`cancel-stop-open-${orderId}`}
        title={due ? t("Confirm where the cancelled set is stored") : t("STOP appears after packing / receiving")}
      >
        🛑 {due ? t("STOP — confirm storage") : t("STOP pending")}
      </Button>
      <CancelStockStopDialog orderId={orderId} open={open} onOpenChange={setOpen} onDone={onDone} />
    </>
  );
}
